import { onMount, type ParentProps, type Ref } from "solid-js";
import { useApi } from "@/api";
import type { Modal as ModalType } from "@/app/context";
import { ModalResetPassword } from "@/components/features/user_settings/mod.tsx";
import { useModals } from "@/contexts/modal.tsx";
import { ModalAttachment } from "./ModalAttachment.tsx";
import { ModalBan } from "./ModalBan.tsx";
import { ModalCameraPreview } from "./ModalCameraPreview.tsx";
import { ModalChannelCreate } from "./ModalChannelCreate";
import { ModalChannelSettings } from "./ModalChannelSettings.tsx";
import { ModalChannelTopic } from "./ModalChannelTopic.tsx";
import { ModalEmojiUpload } from "./ModalEmojiUpload.tsx";
import { ModalExportData } from "./ModalExportData.tsx";
import { ModalInviteCreate } from "./ModalInviteCreate.tsx";
import { ModalKick } from "./ModalKick.tsx";
import { ModalLink } from "./ModalLink.tsx";
import { ModalLogout } from "./ModalLogout.tsx";
import { ModalMedia } from "./ModalMedia.tsx";
import { ModalMessageEdits } from "./ModalMessageEdits.tsx";
import { ModalNotifications } from "./ModalNotifications.tsx";
import { ModalPalette } from "./ModalPalette.tsx";
import { ModalPrivacy } from "./ModalPrivacy.tsx";
import { ModalReactions } from "./ModalReactions.tsx";
import { ModalRoomCreateOrJoin } from "./ModalRoomCreateOrJoin";
import { ModalRoomSettings } from "./ModalRoomSettings.tsx";
import { ModalTagEditor } from "./ModalTagEditor.tsx";
import { ModalThreadCreate } from "./ModalThreadCreate.tsx";
import { ModalTimeout } from "./ModalTimeout.tsx";
import { ModalUserSettings } from "./ModalUserSettings.tsx";
import { ModalViewChannelNickname } from "./ModalViewChannelNickname.tsx";

export const Modal = (
	props: ParentProps<{
		onKeyDown?: (e: KeyboardEvent) => void;
		class?: string;
		contentRef?: Ref<HTMLDivElement>;
	}>,
) => {
	const [, modalCtl] = useModals();
	return (
		<div
			class={`modal ${props.class ?? ""}`}
			onKeyDown={props.onKeyDown}
			tabindex="-1"
			autofocus
		>
			<div class="bg" onClick={() => modalCtl.close()}></div>
			<div class="content" ref={props.contentRef}>
				<div class="base"></div>
				<div class="inner" role="dialog" aria-modal>
					{props.children}
				</div>
			</div>
		</div>
	);
};

export type { ModalType };

// Type guard functions for modal type discrimination
function isAlert(
	modal: ModalType,
): modal is Extract<ModalType, { type: "alert" }> {
	return modal.type === "alert";
}

function isConfirm(
	modal: ModalType,
): modal is Extract<ModalType, { type: "confirm" }> {
	return modal.type === "confirm";
}

function isPrompt(
	modal: ModalType,
): modal is Extract<ModalType, { type: "prompt" }> {
	return modal.type === "prompt";
}

function isMedia(
	modal: ModalType,
): modal is Extract<ModalType, { type: "media" }> {
	return modal.type === "media";
}

function isMessageEdits(
	modal: ModalType,
): modal is Extract<ModalType, { type: "message_edits" }> {
	return modal.type === "message_edits";
}

function isResetPassword(
	modal: ModalType,
): modal is Extract<ModalType, { type: "reset_password" }> {
	return modal.type === "reset_password";
}

function isLogout(
	modal: ModalType,
): modal is Extract<ModalType, { type: "logout" }> {
	return modal.type === "logout";
}

function isPalette(
	modal: ModalType,
): modal is Extract<ModalType, { type: "palette" }> {
	return modal.type === "palette";
}

function isChannelCreate(
	modal: ModalType,
): modal is Extract<ModalType, { type: "channel_create" }> {
	return modal.type === "channel_create";
}

function isTagEditor(
	modal: ModalType,
): modal is Extract<ModalType, { type: "tag_editor" }> {
	return modal.type === "tag_editor";
}

function isExportData(
	modal: ModalType,
): modal is Extract<ModalType, { type: "export_data" }> {
	return modal.type === "export_data";
}

function isViewReactions(
	modal: ModalType,
): modal is Extract<ModalType, { type: "view_reactions" }> {
	return modal.type === "view_reactions";
}

function isPrivacy(
	modal: ModalType,
): modal is Extract<ModalType, { type: "privacy" }> {
	return modal.type === "privacy";
}

function isNotifications(
	modal: ModalType,
): modal is Extract<ModalType, { type: "notifications" }> {
	return modal.type === "notifications";
}

function isAttachment(
	modal: ModalType,
): modal is Extract<ModalType, { type: "attachment" }> {
	return modal.type === "attachment";
}

function isInviteCreate(
	modal: ModalType,
): modal is Extract<ModalType, { type: "invite_create" }> {
	return modal.type === "invite_create";
}

function isChannelTopic(
	modal: ModalType,
): modal is Extract<ModalType, { type: "channel_topic" }> {
	return modal.type === "channel_topic";
}

function isLink(
	modal: ModalType,
): modal is Extract<ModalType, { type: "link" }> {
	return modal.type === "link";
}

function isKick(
	modal: ModalType,
): modal is Extract<ModalType, { type: "kick" }> {
	return modal.type === "kick";
}

function isBan(modal: ModalType): modal is Extract<ModalType, { type: "ban" }> {
	return modal.type === "ban";
}

function isTimeout(
	modal: ModalType,
): modal is Extract<ModalType, { type: "timeout" }> {
	return modal.type === "timeout";
}

function isCameraPreview(
	modal: ModalType,
): modal is Extract<ModalType, { type: "camera_preview" }> {
	return modal.type === "camera_preview";
}

function isRoomCreateOrJoin(
	modal: ModalType,
): modal is Extract<ModalType, { type: "room_create_or_join" }> {
	return modal.type === "room_create_or_join";
}

function isEmojiUpload(
	modal: ModalType,
): modal is Extract<ModalType, { type: "emoji_upload" }> {
	return modal.type === "emoji_upload";
}

function isThreadCreate(
	modal: ModalType,
): modal is Extract<ModalType, { type: "thread_create" }> {
	return modal.type === "thread_create";
}

function isViewChannelNickname(
	modal: ModalType,
): modal is Extract<ModalType, { type: "view_channel_nickname" }> {
	return modal.type === "view_channel_nickname";
}

function isUserSettings(
	modal: ModalType,
): modal is Extract<ModalType, { type: "user_settings" }> {
	return modal.type === "user_settings";
}

function isRoomSettings(
	modal: ModalType,
): modal is Extract<ModalType, { type: "room_settings" }> {
	return modal.type === "room_settings";
}

function isChannelSettings(
	modal: ModalType,
): modal is Extract<ModalType, { type: "channel_settings" }> {
	return modal.type === "channel_settings";
}

export function getModal(modal: ModalType) {
	const api2 = useApi();
	if (isAlert(modal)) {
		return <ModalAlert text={modal.text} />;
	}
	if (isConfirm(modal)) {
		return <ModalConfirm text={modal.text} cont={modal.cont} />;
	}
	if (isPrompt(modal)) {
		return <ModalPrompt text={modal.text} cont={modal.cont} />;
	}
	if (isMedia(modal)) {
		return <ModalMedia media={modal.media} />;
	}
	if (isMessageEdits(modal)) {
		return (
			<ModalMessageEdits
				channel_id={modal.channel_id}
				message_id={modal.message_id}
			/>
		);
	}
	if (isResetPassword(modal)) {
		return <ModalResetPassword />;
	}
	if (isLogout(modal)) {
		return <ModalLogout />;
	}
	if (isPalette(modal)) {
		return <ModalPalette />;
	}
	if (isChannelCreate(modal)) {
		return <ModalChannelCreate room_id={modal.room_id} />;
	}
	if (isTagEditor(modal)) {
		return (
			<ModalTagEditor
				forumChannelId={modal.forumChannelId}
				tag={modal.tag}
				onSave={modal.onSave}
				onClose={modal.onClose}
			/>
		);
	}
	if (isExportData(modal)) {
		return <ModalExportData />;
	}
	if (isViewReactions(modal)) {
		return (
			<ModalReactions
				channel_id={modal.channel_id}
				message_id={modal.message_id}
			/>
		);
	}
	if (isPrivacy(modal)) {
		return <ModalPrivacy room_id={modal.room_id} />;
	}
	if (isNotifications(modal)) {
		return <ModalNotifications room_id={modal.room_id} />;
	}
	if (isAttachment(modal)) {
		return (
			<ModalAttachment
				channel_id={modal.channel_id}
				local_id={modal.local_id}
			/>
		);
	}
	if (isInviteCreate(modal)) {
		return (
			<ModalInviteCreate
				channel_id={modal.channel_id}
				room_id={modal.room_id}
			/>
		);
	}
	if (isChannelTopic(modal)) {
		return <ModalChannelTopic channel_id={modal.channel_id} />;
	}
	if (isLink(modal)) {
		return <ModalLink editor={modal.editor} />;
	}
	if (isKick(modal)) {
		return (
			<ModalKick api={api2} room_id={modal.room_id} user_id={modal.user_id} />
		);
	}
	if (isBan(modal)) {
		return (
			<ModalBan api={api2} room_id={modal.room_id} user_id={modal.user_id} />
		);
	}
	if (isTimeout(modal)) {
		return (
			<ModalTimeout
				api={api2}
				room_id={modal.room_id}
				user_id={modal.user_id}
			/>
		);
	}
	if (isCameraPreview(modal)) {
		return <ModalCameraPreview stream={modal.stream} />;
	}
	if (isRoomCreateOrJoin(modal)) {
		return <ModalRoomCreateOrJoin />;
	}

	if (isEmojiUpload(modal)) {
		return <ModalEmojiUpload room_id={modal.room_id} media={modal.media} />;
	}

	if (isThreadCreate(modal)) {
		return (
			<ModalThreadCreate
				room_id={modal.room_id}
				channel_id={modal.channel_id}
			/>
		);
	}
	if (isViewChannelNickname(modal)) {
		return (
			<ModalViewChannelNickname
				initial={modal.initial}
				placeholder={modal.placeholder}
				cont={modal.cont}
			/>
		);
	}
	if (isUserSettings(modal)) {
		return <ModalUserSettings page={modal.page} />;
	}
	if (isRoomSettings(modal)) {
		return <ModalRoomSettings room_id={modal.room_id} page={modal.page} />;
	}
	if (isChannelSettings(modal)) {
		return (
			<ModalChannelSettings channel_id={modal.channel_id} page={modal.page} />
		);
	}
}

const ModalAlert = (props: { text: string }) => {
	const [, modalCtl] = useModals();
	let btn: HTMLButtonElement | undefined;
	onMount(() => btn?.focus());
	return (
		<Modal
			onKeyDown={(e) => {
				if (e.key === "Escape") {
					modalCtl.close();
				}
			}}
		>
			<p>{props.text}</p>
			<div class="bottom">
				<button type="button" class="button" ref={btn} onClick={modalCtl.close}>
					okay!
				</button>
			</div>
		</Modal>
	);
};

const ModalConfirm = (props: {
	text: string;
	cont: (bool: boolean) => void;
}) => {
	const [, modalCtl] = useModals();
	let cancelBtn: HTMLButtonElement | undefined;
	onMount(() => cancelBtn?.focus());
	return (
		<Modal
			onKeyDown={(e) => {
				if (e.key === "Enter") {
					props.cont(true);
					modalCtl.close();
				} else if (e.key === "Escape") {
					props.cont(false);
					modalCtl.close();
				}
			}}
		>
			<p>{props.text}</p>
			<div class="bottom">
				<button
					type="button"
					class="button"
					onClick={() => {
						props.cont(true);
						modalCtl.close();
					}}
				>
					okay!
				</button>
				<button
					type="button"
					class="button"
					ref={cancelBtn}
					onClick={() => {
						props.cont(false);
						modalCtl.close();
					}}
				>
					nevermind...
				</button>
			</div>
		</Modal>
	);
};

const ModalPrompt = (props: {
	text: string;
	cont: (s: string | null) => void;
}) => {
	const [, modalCtl] = useModals();
	let input: HTMLInputElement | undefined;
	onMount(() => input?.focus());
	return (
		<Modal>
			<p>{props.text}</p>
			<div style="height: 8px"></div>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					const form = e.target as HTMLFormElement;
					const input = form.elements.namedItem("text") as HTMLInputElement;
					props.cont(input.value);
					modalCtl.close();
				}}
			>
				<input ref={input} type="text" name="text" />
				<div class="bottom">
					<input type="submit" class="button primary" value="done!"></input>{" "}
					<button
						type="button"
						class="button"
						onClick={() => {
							props.cont(null);
							modalCtl.close();
						}}
					>
						nevermind...
					</button>
				</div>
			</form>
		</Modal>
	);
};
